import React from 'react';
import './ServiceIllustration.css';

/**
 * Animated Vector Illustrations for each of the 8 Service Cards
 * Inspired by modern tech agency design (SoftSora) with lively CSS micro-animations
 */
const ServiceIllustration = ({ type }) => {
  switch (type) {
    case 'web-dev':
      return (
        <div className="service-illustration-wrapper">
          <svg viewBox="0 0 400 200" className="service-svg" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="webGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#a855f7" stopOpacity="0.8"/>
                <stop offset="100%" stopColor="#6366f1" stopOpacity="0.8"/>
              </linearGradient>
              <linearGradient id="screenGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#240832" />
                <stop offset="100%" stopColor="#14021d" />
              </linearGradient>
            </defs>

            {/* Desktop Screen Base */}
            <rect x="70" y="25" width="260" height="135" rx="10" fill="url(#screenGrad)" stroke="#a855f7" strokeWidth="1.5" />
            <path d="M165 160 L150 185 L250 185 L235 160 Z" fill="#2d0a3d" stroke="#a855f7" strokeWidth="1"/>
            <rect x="135" y="185" width="130" height="6" rx="3" fill="#a855f7" opacity="0.6"/>

            {/* Browser Header */}
            <rect x="70" y="25" width="260" height="22" rx="10" fill="#3b0f50" />
            <circle cx="85" cy="36" r="3.5" fill="#f43f5e" />
            <circle cx="97" cy="36" r="3.5" fill="#fbbf24" />
            <circle cx="109" cy="36" r="3.5" fill="#34d399" />
            <rect x="125" y="30" width="140" height="12" rx="6" fill="#1d0428" opacity="0.7"/>

            {/* Code Window on Left */}
            <g className="anim-float-subtle">
              <rect x="85" y="58" width="105" height="90" rx="6" fill="#1b0324" stroke="#a855f7" strokeWidth="1" strokeDasharray="3,3"/>
              <text x="95" y="76" fill="#c084fc" fontSize="9" fontFamily="monospace">&lt;App /&gt;</text>
              <rect x="95" y="84" width="70" height="4" rx="2" fill="#c084fc" opacity="0.5" />
              <rect x="95" y="93" width="50" height="4" rx="2" fill="#60a5fa" opacity="0.5" />
              <rect x="95" y="102" width="80" height="4" rx="2" fill="#34d399" opacity="0.5" />
              <rect x="95" y="111" width="40" height="4" rx="2" fill="#f43f5e" opacity="0.5" />
              <line x1="95" y1="126" x2="105" y2="126" stroke="#c084fc" strokeWidth="2" className="anim-blink"/>
            </g>

            {/* Web UI Elements on Right */}
            <g>
              <rect x="202" y="58" width="115" height="42" rx="6" fill="#360c49" />
              <circle cx="218" cy="74" r="8" fill="#a855f7" opacity="0.6"/>
              <rect x="232" y="68" width="55" height="5" rx="2" fill="#ffffff" opacity="0.8"/>
              <rect x="232" y="78" width="40" height="4" rx="2" fill="#9ca3af" opacity="0.6"/>

              {/* Dynamic Bar Charts */}
              <rect x="202" y="108" width="24" height="40" rx="4" fill="#a855f7" className="anim-bar-1"/>
              <rect x="232" y="96" width="24" height="52" rx="4" fill="#c084fc" className="anim-bar-2"/>
              <rect x="262" y="116" width="24" height="32" rx="4" fill="#60a5fa" className="anim-bar-3"/>
              <rect x="292" y="102" width="24" height="46" rx="4" fill="#818cf8" className="anim-bar-4"/>
            </g>

            {/* Floating Tag */}
            <g className="anim-float-tag">
              <rect x="245" y="14" width="75" height="24" rx="12" fill="#9333ea" filter="drop-shadow(0 4px 10px rgba(0,0,0,0.5))"/>
              <text x="257" y="30" fill="#ffffff" fontSize="10" fontWeight="bold">React 18</text>
            </g>

            {/* Character sitting with laptop */}
            <g transform="translate(30, 95)" className="anim-character">
              <ellipse cx="25" cy="85" rx="15" ry="5" fill="#000000" opacity="0.4"/>
              {/* Torso */}
              <path d="M15 60 C15 45, 35 45, 35 60 L32 80 L18 80 Z" fill="#9333ea"/>
              {/* Head */}
              <circle cx="25" cy="38" r="11" fill="#fbcfe8"/>
              <path d="M16 35 C16 26, 34 26, 34 35 C32 30, 20 30, 16 35 Z" fill="#4a044e"/>
              {/* Legs */}
              <path d="M18 78 L5 83 L14 87 Z" fill="#581c87"/>
              <path d="M32 78 L45 83 L36 87 Z" fill="#581c87"/>
              {/* Mini Laptop */}
              <polygon points="20,68 38,68 35,74 23,74" fill="#e9d5ff"/>
              <rect x="22" y="60" width="14" height="9" rx="1" fill="#60a5fa" opacity="0.9"/>
            </g>
          </svg>
        </div>
      );

    case 'mobile-dev':
      return (
        <div className="service-illustration-wrapper">
          <svg viewBox="0 0 400 200" className="service-svg" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="mobGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#2c093a"/>
                <stop offset="100%" stopColor="#15021e"/>
              </linearGradient>
            </defs>

            {/* Left Mobile Device */}
            <g transform="translate(100, 15)">
              <rect x="0" y="0" width="90" height="165" rx="18" fill="url(#mobGrad)" stroke="#c084fc" strokeWidth="1.5"/>
              {/* Speaker / Notch */}
              <rect x="30" y="8" width="30" height="4" rx="2" fill="#581c87"/>
              <circle cx="68" cy="10" r="2" fill="#60a5fa"/>

              {/* App UI */}
              <rect x="10" y="24" width="70" height="35" rx="8" fill="#4a044e"/>
              <circle cx="25" cy="41" r="7" fill="#c084fc"/>
              <rect x="38" y="36" width="32" height="4" rx="2" fill="#ffffff" opacity="0.9"/>
              <rect x="38" y="44" width="22" height="3" rx="1.5" fill="#d8b4fe" opacity="0.6"/>

              {/* Grid Cards inside mobile */}
              <rect x="10" y="68" width="32" height="40" rx="6" fill="#3b0764"/>
              <rect x="48" y="68" width="32" height="40" rx="6" fill="#3b0764"/>
              <rect x="10" y="114" width="70" height="28" rx="6" fill="#701a75" className="anim-pulse-glow"/>
              <circle cx="45" cy="154" r="5" fill="#a855f7" opacity="0.5"/>
            </g>

            {/* Right Mobile Device (Angled / Shifted) */}
            <g transform="translate(210, 30)" className="anim-float-subtle">
              <rect x="0" y="0" width="85" height="150" rx="16" fill="#20052a" stroke="#60a5fa" strokeWidth="1.2"/>
              <rect x="28" y="6" width="28" height="3" rx="1.5" fill="#3b0764"/>
              
              {/* Chat / Feed App UI */}
              <rect x="10" y="20" width="45" height="14" rx="7" fill="#60a5fa" opacity="0.8"/>
              <rect x="30" y="42" width="45" height="14" rx="7" fill="#c084fc" opacity="0.8"/>
              <rect x="10" y="64" width="50" height="14" rx="7" fill="#60a5fa" opacity="0.8"/>
              
              <circle cx="42" cy="110" r="18" fill="#7c3aed" opacity="0.6" className="anim-pulse-circle"/>
              <path d="M36 110 L48 110 M42 104 L42 116" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round"/>
            </g>

            {/* Floating Notification Badge */}
            <g className="anim-float-badge">
              <rect x="145" y="75" width="110" height="32" rx="16" fill="#9333ea" stroke="#ffffff" strokeWidth="1" filter="drop-shadow(0 6px 15px rgba(0,0,0,0.6))"/>
              <circle cx="163" cy="91" r="7" fill="#34d399"/>
              <text x="176" y="94" fill="#ffffff" fontSize="9" fontWeight="bold">Flutter 60 FPS</text>
            </g>

            {/* Developer Figure on side */}
            <g transform="translate(305, 75)" className="anim-character">
              <ellipse cx="25" cy="95" rx="16" ry="5" fill="#000000" opacity="0.4"/>
              <path d="M15 45 C15 35, 35 35, 35 45 L32 75 L18 75 Z" fill="#db2777"/>
              <circle cx="25" cy="25" r="10" fill="#fbcfe8"/>
              <path d="M16 22 C16 14, 34 14, 34 22 Z" fill="#374151"/>
              {/* Holding Phone */}
              <rect x="30" y="42" width="10" height="18" rx="2" fill="#60a5fa"/>
            </g>
          </svg>
        </div>
      );

    case 'backend-cloud':
      return (
        <div className="service-illustration-wrapper">
          <svg viewBox="0 0 400 200" className="service-svg" xmlns="http://www.w3.org/2000/svg">
            {/* Server Rack on Right */}
            <g transform="translate(245, 35)">
              <rect x="0" y="0" width="105" height="135" rx="8" fill="#1b0324" stroke="#a855f7" strokeWidth="1.5"/>
              {/* Server Shelves */}
              <rect x="8" y="12" width="89" height="24" rx="4" fill="#2d0a3d" stroke="#c084fc" strokeWidth="0.8"/>
              <circle cx="20" cy="24" r="3" fill="#34d399" className="anim-led-1"/>
              <circle cx="28" cy="24" r="3" fill="#60a5fa" className="anim-led-2"/>
              <line x1="40" y1="24" x2="85" y2="24" stroke="#a855f7" strokeWidth="2" strokeDasharray="4,3"/>

              <rect x="8" y="44" width="89" height="24" rx="4" fill="#2d0a3d" stroke="#c084fc" strokeWidth="0.8"/>
              <circle cx="20" cy="56" r="3" fill="#34d399" className="anim-led-2"/>
              <circle cx="28" cy="56" r="3" fill="#f43f5e" className="anim-led-3"/>
              <line x1="40" y1="56" x2="85" y2="56" stroke="#a855f7" strokeWidth="2" strokeDasharray="4,3"/>

              <rect x="8" y="76" width="89" height="24" rx="4" fill="#2d0a3d" stroke="#c084fc" strokeWidth="0.8"/>
              <circle cx="20" cy="88" r="3" fill="#34d399" className="anim-led-3"/>
              <circle cx="28" cy="88" r="3" fill="#60a5fa" className="anim-led-1"/>
              <line x1="40" y1="88" x2="85" y2="88" stroke="#a855f7" strokeWidth="2" strokeDasharray="4,3"/>

              <rect x="8" y="108" width="89" height="18" rx="4" fill="#2d0a3d" opacity="0.6"/>
            </g>

            {/* Central Rotating Gear with API Badge */}
            <g transform="translate(150, 75)">
              <g className="anim-rotate-gear">
                <circle cx="35" cy="35" r="32" fill="#4a044e" stroke="#c084fc" strokeWidth="2.5"/>
                {/* Gear Teeth */}
                <rect x="30" y="-3" width="10" height="8" rx="2" fill="#c084fc"/>
                <rect x="30" y="65" width="10" height="8" rx="2" fill="#c084fc"/>
                <rect x="-3" y="30" width="8" height="10" rx="2" fill="#c084fc"/>
                <rect x="65" y="30" width="8" height="10" rx="2" fill="#c084fc"/>
                <rect x="5" y="5" width="9" height="9" rx="2" fill="#c084fc" transform="rotate(45 9.5 9.5)"/>
                <rect x="55" y="5" width="9" height="9" rx="2" fill="#c084fc" transform="rotate(45 59.5 9.5)"/>
                <rect x="5" y="55" width="9" height="9" rx="2" fill="#c084fc" transform="rotate(45 9.5 59.5)"/>
                <rect x="55" y="55" width="9" height="9" rx="2" fill="#c084fc" transform="rotate(45 59.5 59.5)"/>
              </g>
              <circle cx="35" cy="35" r="20" fill="#1b0324" stroke="#ffffff" strokeWidth="1.5"/>
              <text x="24" y="39" fill="#ffffff" fontSize="11" fontWeight="bold" letterSpacing="0.5">API</text>
            </g>

            {/* Floating Cloud Node */}
            <g transform="translate(55, 30)" className="anim-float-cloud">
              <path d="M25 40 A15 15 0 0 1 50 25 A25 25 0 0 1 95 30 A18 18 0 0 1 105 50 A15 15 0 0 1 90 65 L25 65 A15 15 0 0 1 25 40 Z" fill="#701a75" stroke="#e879f9" strokeWidth="1.5"/>
              <path d="M55 48 L65 38 L75 48 M65 38 L65 58" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </g>

            {/* Connecting Circuit Lines */}
            <path d="M120 60 L145 60 L145 90 L155 90" stroke="#a855f7" strokeWidth="2" strokeDasharray="4,4" fill="none" className="anim-flow-line"/>
            <path d="M215 110 L245 110" stroke="#a855f7" strokeWidth="2" strokeDasharray="4,4" fill="none" className="anim-flow-line"/>

            {/* Engineer seated with laptop (matches user image) */}
            <g transform="translate(50, 105)" className="anim-character">
              <ellipse cx="25" cy="75" rx="18" ry="5" fill="#000000" opacity="0.4"/>
              <path d="M15 35 C15 22, 35 22, 35 35 L32 65 L18 65 Z" fill="#9333ea"/>
              <circle cx="25" cy="16" r="10" fill="#fbcfe8"/>
              <path d="M16 13 C16 6, 34 6, 34 13 Z" fill="#1f2937"/>
              <polygon points="18,52 38,52 35,60 21,60" fill="#e9d5ff"/>
              <rect x="22" y="44" width="14" height="9" rx="1" fill="#c084fc"/>
            </g>
          </svg>
        </div>
      );

    case 'ai-solutions':
      return (
        <div className="service-illustration-wrapper">
          <svg viewBox="0 0 400 200" className="service-svg" xmlns="http://www.w3.org/2000/svg">
            {/* Central Glowing AI Core */}
            <g transform="translate(160, 45)">
              <circle cx="40" cy="40" r="38" fill="rgba(168, 85, 247, 0.15)" className="anim-pulse-circle"/>
              <circle cx="40" cy="40" r="28" fill="#2d0a3d" stroke="#c084fc" strokeWidth="2"/>
              {/* Brain / Circuit icon inside core */}
              <path d="M30 40 C30 30, 50 30, 50 40 C50 50, 30 50, 30 40 Z" stroke="#60a5fa" strokeWidth="2" fill="none"/>
              <circle cx="40" cy="40" r="5" fill="#ffffff"/>
              <circle cx="28" cy="40" r="3" fill="#a855f7"/>
              <circle cx="52" cy="40" r="3" fill="#a855f7"/>
              <circle cx="40" cy="28" r="3" fill="#60a5fa"/>
              <circle cx="40" cy="52" r="3" fill="#60a5fa"/>
            </g>

            {/* Neural Connections & Satellite Nodes */}
            <g className="anim-neural-grid">
              {/* Node 1 */}
              <line x1="80" y1="50" x2="165" y2="70" stroke="#a855f7" strokeWidth="1.5" strokeDasharray="3,3"/>
              <circle cx="80" cy="50" r="16" fill="#3b0764" stroke="#c084fc" strokeWidth="1.5"/>
              <text x="73" y="54" fill="#ffffff" fontSize="12">🧠</text>

              {/* Node 2 */}
              <line x1="100" y1="130" x2="170" y2="105" stroke="#60a5fa" strokeWidth="1.5" strokeDasharray="3,3"/>
              <circle cx="100" cy="130" r="16" fill="#1e1b4b" stroke="#60a5fa" strokeWidth="1.5"/>
              <text x="93" y="134" fill="#ffffff" fontSize="12">⚡</text>

              {/* Node 3 */}
              <line x1="310" y1="55" x2="235" y2="75" stroke="#a855f7" strokeWidth="1.5" strokeDasharray="3,3"/>
              <circle cx="310" cy="55" r="16" fill="#3b0764" stroke="#c084fc" strokeWidth="1.5"/>
              <text x="303" y="59" fill="#ffffff" fontSize="12">🤖</text>

              {/* Node 4 */}
              <line x1="295" y1="135" x2="230" y2="110" stroke="#34d399" strokeWidth="1.5" strokeDasharray="3,3"/>
              <circle cx="295" cy="135" r="16" fill="#064e3b" stroke="#34d399" strokeWidth="1.5"/>
              <text x="288" y="139" fill="#ffffff" fontSize="12">📊</text>
            </g>

            {/* Floating Banner */}
            <g className="anim-float-badge" transform="translate(130, 140)">
              <rect x="0" y="0" width="140" height="28" rx="14" fill="#7c3aed" stroke="#ffffff" strokeWidth="1"/>
              <circle cx="18" cy="14" r="5" fill="#34d399"/>
              <text x="32" y="18" fill="#ffffff" fontSize="10" fontWeight="bold">LLM &amp; Automation</text>
            </g>
          </svg>
        </div>
      );

    case 'consulting':
      return (
        <div className="service-illustration-wrapper">
          <svg viewBox="0 0 400 200" className="service-svg" xmlns="http://www.w3.org/2000/svg">
            {/* Architecture Blueprint Board */}
            <g transform="translate(85, 25)">
              <rect x="0" y="0" width="230" height="145" rx="12" fill="#1e102d" stroke="#a855f7" strokeWidth="1.5"/>
              <rect x="0" y="0" width="230" height="22" rx="12" fill="#3b0f50"/>
              <text x="16" y="16" fill="#d8b4fe" fontSize="10" fontWeight="bold">SYSTEM ROADMAP &amp; ARCHITECTURE</text>

              {/* Blueprint Flow Chart */}
              <rect x="25" y="45" width="48" height="26" rx="6" fill="#581c87" stroke="#c084fc" strokeWidth="1"/>
              <text x="33" y="61" fill="#ffffff" fontSize="8">CLIENT</text>

              <line x1="73" y1="58" x2="105" y2="58" stroke="#a855f7" strokeWidth="2" strokeDasharray="3,3"/>

              <rect x="105" y="40" width="56" height="36" rx="6" fill="#701a75" stroke="#e879f9" strokeWidth="1"/>
              <text x="114" y="61" fill="#ffffff" fontSize="8">GATEWAY</text>

              <line x1="161" y1="58" x2="185" y2="58" stroke="#a855f7" strokeWidth="2" strokeDasharray="3,3"/>

              <rect x="185" y="45" width="40" height="26" rx="6" fill="#1e3a8a" stroke="#60a5fa" strokeWidth="1"/>
              <text x="193" y="61" fill="#ffffff" fontSize="8">CLOUD</text>

              {/* Lower level database blueprint */}
              <line x1="133" y1="76" x2="133" y2="98" stroke="#c084fc" strokeWidth="2"/>
              <rect x="105" y="98" width="56" height="30" rx="6" fill="#3b0764" stroke="#c084fc" strokeWidth="1"/>
              <text x="114" y="117" fill="#ffffff" fontSize="8">DATABASE</text>
            </g>

            {/* Floating Lightbulb Insight Badge */}
            <g className="anim-float-tag" transform="translate(260, 10)">
              <circle cx="20" cy="20" r="18" fill="#fbbf24" filter="drop-shadow(0 4px 12px rgba(251,191,36,0.5))"/>
              <text x="11" y="27" fontSize="18">💡</text>
            </g>

            {/* Tech Consultant Figure on left */}
            <g transform="translate(30, 85)" className="anim-character">
              <ellipse cx="25" cy="85" rx="16" ry="5" fill="#000000" opacity="0.4"/>
              <path d="M15 35 C15 22, 35 22, 35 35 L32 68 L18 68 Z" fill="#6366f1"/>
              <circle cx="25" cy="16" r="10" fill="#fed7aa"/>
              <path d="M16 13 C16 6, 34 6, 34 13 Z" fill="#1e293b"/>
              {/* Pointing pointer */}
              <line x1="33" y1="40" x2="52" y2="28" stroke="#fed7aa" strokeWidth="3" strokeLinecap="round"/>
            </g>
          </svg>
        </div>
      );

    case 'qa-testing':
      return (
        <div className="service-illustration-wrapper">
          <svg viewBox="0 0 400 200" className="service-svg" xmlns="http://www.w3.org/2000/svg">
            {/* Testing Radar Dashboard Screen */}
            <g transform="translate(100, 25)">
              <rect x="0" y="0" width="210" height="145" rx="12" fill="#1a0422" stroke="#34d399" strokeWidth="1.5"/>
              <rect x="0" y="0" width="210" height="22" rx="12" fill="#064e3b"/>
              <text x="16" y="16" fill="#a7f3d0" fontSize="10" fontWeight="bold">QA &amp; SECURITY STRESS TEST</text>

              {/* Radar Grid Circles */}
              <circle cx="105" cy="85" r="45" fill="none" stroke="#059669" strokeWidth="0.8" opacity="0.4"/>
              <circle cx="105" cy="85" r="30" fill="none" stroke="#059669" strokeWidth="0.8" opacity="0.6"/>
              <circle cx="105" cy="85" r="15" fill="none" stroke="#059669" strokeWidth="0.8" opacity="0.8"/>
              <circle cx="105" cy="85" r="3" fill="#34d399"/>

              {/* Radar Sweeper */}
              <line x1="105" y1="85" x2="148" y2="60" stroke="#34d399" strokeWidth="2" className="anim-rotate-radar"/>

              {/* Passed Tests Indicators */}
              <g transform="translate(20, 40)">
                <circle cx="10" cy="10" r="5" fill="#34d399"/>
                <text x="22" y="13" fill="#ffffff" fontSize="9">Unit Tests: 100%</text>

                <circle cx="10" cy="30" r="5" fill="#34d399"/>
                <text x="22" y="33" fill="#ffffff" fontSize="9">APIs: 200 OK</text>

                <circle cx="10" cy="50" r="5" fill="#34d399"/>
                <text x="22" y="53" fill="#ffffff" fontSize="9">Speed: 99/100</text>
              </g>
            </g>

            {/* Magnifying Glass (matches user image) */}
            <g transform="translate(265, 80)" className="anim-float-subtle">
              <circle cx="28" cy="28" r="22" fill="rgba(52, 211, 153, 0.15)" stroke="#34d399" strokeWidth="3"/>
              <line x1="44" y1="44" x2="68" y2="68" stroke="#34d399" strokeWidth="5" strokeLinecap="round"/>
              <text x="19" y="34" fill="#34d399" fontSize="16" fontWeight="bold">✓</text>
            </g>

            {/* QA Engineer Inspecting with tablet */}
            <g transform="translate(40, 85)" className="anim-character">
              <ellipse cx="25" cy="85" rx="16" ry="5" fill="#000000" opacity="0.4"/>
              <path d="M15 35 C15 22, 35 22, 35 35 L32 68 L18 68 Z" fill="#059669"/>
              <circle cx="25" cy="16" r="10" fill="#fecdd3"/>
              <path d="M16 13 C16 6, 34 6, 34 13 Z" fill="#1f2937"/>
              {/* Holding Clipboard */}
              <rect x="28" y="38" width="14" height="20" rx="2" fill="#ffffff"/>
              <line x1="31" y1="44" x2="39" y2="44" stroke="#059669" strokeWidth="1.5"/>
              <line x1="31" y1="49" x2="39" y2="49" stroke="#059669" strokeWidth="1.5"/>
            </g>
          </svg>
        </div>
      );

    case 'uiux-design':
      return (
        <div className="service-illustration-wrapper">
          <svg viewBox="0 0 400 200" className="service-svg" xmlns="http://www.w3.org/2000/svg">
            {/* Design Artboard Tablet */}
            <g transform="translate(90, 25)">
              <rect x="0" y="0" width="225" height="145" rx="12" fill="#1e0b29" stroke="#f43f5e" strokeWidth="1.5"/>
              <rect x="0" y="0" width="225" height="22" rx="12" fill="#4c0519"/>
              <text x="16" y="16" fill="#fecdd3" fontSize="10" fontWeight="bold">UI/UX FIGMA WORKSPACE</text>

              {/* Wireframe Card Elements */}
              <rect x="18" y="38" width="85" height="48" rx="8" fill="#831843" opacity="0.7"/>
              <circle cx="34" cy="54" r="8" fill="#fb7185"/>
              <rect x="48" y="50" width="45" height="4" rx="2" fill="#ffffff"/>
              <rect x="48" y="58" width="30" height="3" rx="1.5" fill="#fbcfe8"/>

              <rect x="18" y="94" width="85" height="40" rx="8" fill="#500724"/>
              <line x1="28" y1="114" x2="88" y2="114" stroke="#f43f5e" strokeWidth="2" strokeDasharray="3,3"/>

              {/* Mobile Prototype Frame on Right */}
              <rect x="125" y="38" width="80" height="96" rx="10" fill="#290518" stroke="#f43f5e" strokeWidth="1"/>
              <rect x="135" y="48" width="60" height="28" rx="4" fill="#fb7185" opacity="0.8"/>
              <rect x="135" y="82" width="60" height="8" rx="2" fill="#ffffff" opacity="0.7"/>
              <rect x="135" y="95" width="40" height="6" rx="2" fill="#f43f5e"/>
            </g>

            {/* Designer Pen Tool Cursor */}
            <g transform="translate(190, 75)" className="anim-float-subtle">
              <path d="M0 0 L18 12 L11 15 L15 25 L10 27 L6 17 L0 20 Z" fill="#ffffff" stroke="#f43f5e" strokeWidth="1.5" filter="drop-shadow(0 4px 8px rgba(0,0,0,0.5))"/>
            </g>

            {/* Color Palette Swatches */}
            <g className="anim-float-badge" transform="translate(285, 45)">
              <rect x="0" y="0" width="75" height="26" rx="13" fill="#37103f" stroke="#ffffff" strokeWidth="1"/>
              <circle cx="15" cy="13" r="6" fill="#a855f7"/>
              <circle cx="32" cy="13" r="6" fill="#ec4899"/>
              <circle cx="49" cy="13" r="6" fill="#60a5fa"/>
              <circle cx="64" cy="13" r="4" fill="#34d399"/>
            </g>
          </svg>
        </div>
      );

    case 'documentation':
      return (
        <div className="service-illustration-wrapper">
          <svg viewBox="0 0 400 200" className="service-svg" xmlns="http://www.w3.org/2000/svg">
            {/* Central Documentation Binder / Book */}
            <g transform="translate(115, 25)">
              <rect x="0" y="0" width="175" height="145" rx="10" fill="#1b0324" stroke="#c084fc" strokeWidth="1.5"/>
              <rect x="0" y="0" width="30" height="145" rx="10" fill="#4a044e"/>
              <line x1="30" y1="0" x2="30" y2="145" stroke="#a855f7" strokeWidth="2"/>

              {/* Book Spine Rings */}
              <circle cx="15" cy="30" r="4" fill="#c084fc"/>
              <circle cx="15" cy="65" r="4" fill="#c084fc"/>
              <circle cx="15" cy="100" r="4" fill="#c084fc"/>
              <circle cx="15" cy="130" r="4" fill="#c084fc"/>

              {/* Documentation Content Lines */}
              <text x="45" y="30" fill="#ffffff" fontSize="11" fontWeight="bold">SRS &amp; API SPEC</text>
              <rect x="45" y="44" width="115" height="4" rx="2" fill="#c084fc" opacity="0.6"/>
              <rect x="45" y="54" width="95" height="4" rx="2" fill="#9ca3af" opacity="0.6"/>
              <rect x="45" y="64" width="110" height="4" rx="2" fill="#9ca3af" opacity="0.6"/>

              {/* Flowchart Diagram on page */}
              <rect x="45" y="80" width="34" height="20" rx="4" fill="#581c87" stroke="#c084fc" strokeWidth="0.8"/>
              <text x="50" y="93" fill="#ffffff" fontSize="7">START</text>
              <line x1="79" y1="90" x2="98" y2="90" stroke="#c084fc" strokeWidth="1.5"/>
              <rect x="98" y="80" width="42" height="20" rx="4" fill="#1e3a8a" stroke="#60a5fa" strokeWidth="0.8"/>
              <text x="104" y="93" fill="#ffffff" fontSize="7">PROCESS</text>

              <line x1="45" y1="116" x2="145" y2="116" stroke="#a855f7" strokeWidth="1" strokeDasharray="3,3"/>
              <text x="45" y="132" fill="#34d399" fontSize="9" fontWeight="bold">✔ IEEE VERIFIED</text>
            </g>

            {/* Official Stamp / Seal */}
            <g transform="translate(265, 95)" className="anim-float-subtle">
              <circle cx="30" cy="30" r="26" fill="rgba(168, 85, 247, 0.2)" stroke="#a855f7" strokeWidth="2" strokeDasharray="4,2"/>
              <circle cx="30" cy="30" r="20" fill="#6b21a8"/>
              <text x="16" y="34" fill="#ffffff" fontSize="9" fontWeight="bold">OFFICIAL</text>
            </g>

            {/* Technical Writer Figure on left */}
            <g transform="translate(45, 80)" className="anim-character">
              <ellipse cx="25" cy="85" rx="16" ry="5" fill="#000000" opacity="0.4"/>
              <path d="M15 35 C15 22, 35 22, 35 35 L32 68 L18 68 Z" fill="#7e22ce"/>
              <circle cx="25" cy="16" r="10" fill="#fbcfe8"/>
              <path d="M16 13 C16 6, 34 6, 34 13 Z" fill="#374151"/>
              {/* Pen in hand */}
              <line x1="33" y1="45" x2="48" y2="38" stroke="#fbbf24" strokeWidth="2.5" strokeLinecap="round"/>
            </g>
          </svg>
        </div>
      );

    default:
      return null;
  }
};

export default ServiceIllustration;
